import { useFeatureFlagEnabled } from "posthog-js/react";
import { Fragment, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useTenant } from "@/providers/session";

import { usePermissions } from "@/hooks/usePermissions";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { useLocation } from "@tanstack/react-router";
import { SuperAdminOnly } from "../shared/miscellaneous/superadmin-only";

import {
  Building2,
  Cable,
  CloudDownload,
  Landmark,
  Mail,
  MessageCircle,
  Settings2,
  Target,
  Users,
  Wallet,
} from "lucide-react";
import Navbar from "../shared/app-navigation/navbar";
import { Link } from "../ui/link";
import { CompanyInit } from "./company-init";

type Props = PropsWithChildren;

type SettingsItem = {
  text: string;
  pathname: string;
  superAdminOnly?: boolean;
  query: { company: string | undefined };
  icon?: React.ElementType;
};

type SettingsSection = {
  label?: string;
  items: SettingsItem[];
};

const _SideBar = ({ sections }: { sections: SettingsSection[] }) => {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <Sidebar
      collapsible="none"
      className="w-[240px] flex-none border-r bg-background"
    >
      <SidebarContent>
        {sections.map((section, sectionIndex) => (
          <SidebarGroup key={section.label ?? sectionIndex}>
            {section.label && (
              <SidebarGroupLabel className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                {section.label}
              </SidebarGroupLabel>
            )}
            <SidebarMenu>
              {section.items.map((item) => {
                const Wrapper = item.superAdminOnly ? SuperAdminOnly : Fragment;
                const IconComponent = item.icon;

                return (
                  <Wrapper key={item.text}>
                    <SidebarMenuItem key={item.text}>
                      <Link
                        href={{
                          pathname: item.pathname,
                          query: item.query,
                        }}
                        className="w-full"
                        prefetch
                      >
                        <SidebarMenuButton
                          className="whitespace-nowrap"
                          isActive={pathname.includes(item.pathname)}
                        >
                          {IconComponent && (
                            <IconComponent className="h-4 w-4" />
                          )}
                          {item.text}
                        </SidebarMenuButton>
                      </Link>
                    </SidebarMenuItem>
                  </Wrapper>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

const ClientSideBar = () => {
  const { t } = useTranslation();
  const { companySlug, hasCostCenters } = useTenant();
  const whatsappIntegrationEnabled = useFeatureFlagEnabled(
    "whatsapp-integration",
  );

  const sections: SettingsSection[] = [
    {
      label: t("settings.sections.master_data") as string,
      items: [
        {
          text: t("creditors.link") as string,
          pathname: "/$company/unternehmen/einstellungen/kreditoren",
          query: { company: companySlug },
          icon: Users,
        },
        {
          text: t("debtors.link") as string,
          pathname: "/$company/unternehmen/einstellungen/debitoren",
          query: { company: companySlug },
          icon: Users,
        },
        ...(hasCostCenters
          ? [
              {
                text: t("cost_centers.link") as string,
                pathname: "/$company/unternehmen/einstellungen/kostenstellen",
                query: { company: companySlug },
                icon: Target,
              },
            ]
          : []),
      ],
    },
    {
      label: t("settings.sections.integrations") as string,
      items: [
        {
          text: t("bank.link") as string,
          pathname: "/$company/unternehmen/einstellungen/bank",
          query: { company: companySlug },
          icon: Landmark,
        },
        {
          text: t("forwarding.link") as string,
          pathname: "/$company/unternehmen/einstellungen/weiterleitung",
          query: { company: companySlug },
          icon: Mail,
        },
        ...(whatsappIntegrationEnabled
          ? [
              {
                text: t("forwarding.whatsapp.link") as string,
                pathname:
                  "/$company/unternehmen/einstellungen/whatsapp-weiterleitung",
                query: { company: companySlug },
                icon: MessageCircle,
              },
            ]
          : []),
      ],
    },
  ];

  return <_SideBar sections={sections} />;
};

const AccountantSideBar = () => {
  const { t } = useTranslation();
  const { companySlug, hasCostCenters, invoiceInboxEnabled } = useTenant();
  const { isSuperAdmin } = usePermissions();
  const whatsappIntegrationEnabled = useFeatureFlagEnabled(
    "whatsapp-integration",
  );

  const sections: SettingsSection[] = [
    {
      label: t("settings.sections.master_data") as string,
      items: [
        {
          text: t("basic_data.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/eckdaten",
          query: { company: companySlug },
          icon: Building2,
        },
        {
          text: t("creditors.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/kreditoren",
          query: { company: companySlug },
          icon: Users,
        },
        {
          text: t("debtors.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/debitoren",
          query: { company: companySlug },
          icon: Users,
        },
        {
          text: t("accounts.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/konten",
          query: { company: companySlug },
          icon: Wallet,
        },
        ...(hasCostCenters
          ? [
              {
                text: t("cost_centers.link") as string,
                pathname: "/$company/buchhaltung/einstellungen/kostenstellen",
                query: { company: companySlug },
                icon: Target,
              },
            ]
          : []),
        {
          text: t("configuration.title") as string,
          pathname: "/$company/buchhaltung/einstellungen/konfiguration",
          query: { company: companySlug },
          icon: Settings2,
        },
      ],
    },
    {
      label: t("settings.sections.integrations") as string,
      items: [
        {
          text: t("integrations.datev.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/integrationen",
          query: { company: companySlug },
          icon: Cable,
        },
        ...(invoiceInboxEnabled || isSuperAdmin
          ? [
              {
                text: t("bank.link") as string,
                pathname: "/$company/unternehmen/einstellungen/bank",
                query: { company: companySlug },
                icon: Landmark,
              },
            ]
          : []),
        {
          text: t("forwarding.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/weiterleitung",
          query: { company: companySlug },
          icon: Mail,
        },
        ...(whatsappIntegrationEnabled
          ? [
              {
                text: t("forwarding.whatsapp.link") as string,
                pathname:
                  "/$company/buchhaltung/einstellungen/whatsapp-weiterleitung",
                query: { company: companySlug },
                icon: MessageCircle,
              },
            ]
          : []),
        {
          text: t("integrations.kanzleiland.link") as string,
          pathname: "/$company/buchhaltung/einstellungen/kanzleiland",
          query: { company: companySlug },
          icon: CloudDownload,
        },
      ],
    },
  ];

  return <_SideBar sections={sections} />;
};

const SideBar = () => {
  const { role } = useTenant();

  if (role === CompanyRole.CLIENT) {
    return <ClientSideBar />;
  }

  return <AccountantSideBar />;
};

export const SettingsLayout = ({ children }: Props) => {
  const routeLocation = useLocation();
  const pathname = routeLocation.pathname;

  const location = pathname.split("/").pop() ?? "";

  return (
    <CompanyInit>
      <div className="flex flex-col h-full w-full">
        <Navbar location={["settings", location]} />
        <div className="flex-1 flex flex-row overflow-hidden w-full">
          <SideBar />
          <div className="h-full overflow-auto bg-slate-50 w-full">
            <div className="flex flex-col w-full h-full p-4 overflow-auto">
              {children}
            </div>
          </div>
        </div>
      </div>
    </CompanyInit>
  );
};
